import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Divider,
  FormControl,
  FormLabel,
  HStack,
  List,
  ListItem,
  Progress,
  Text,
  useColorModeValue,
  useToast,
  VStack,
} from "@chakra-ui/react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useEffect, useRef, useState } from "react"
import { ProductFilesService } from "../../../api/productFiles"
import {
  normalizeProductUnitWeight,
  type ProductCreateWithUnitWeight,
} from "../../../api/productUnitWeight"
import {
  type ProductCreateDTO,
  type ProductResponceDTO,
  ProductService,
} from "../../../client"
import { extraDataToJson } from "./ExtraDataFields"
import ProductFormFields, {
  createEmptyProductFormState,
  type ProductFormState,
} from "./ProductFormFields"

type Props = {
  onSuccess?: (product: ProductResponceDTO) => void
  onCancel?: () => void
}

type SKUConflict = {
  requested: string
  suggested: string | null
}

export default function AddProduct({ onSuccess, onCancel }: Props) {
  const lastManufacturerStorageKey = "wms.products.last-manufacturer"
  const toast = useToast()
  const qc = useQueryClient()
  const panelBg = useColorModeValue("gray.50", "whiteAlpha.50")
  const panelBorder = useColorModeValue("gray.200", "whiteAlpha.200")
  const mutedText = useColorModeValue("gray.600", "gray.400")

  const [form, setForm] = useState<ProductFormState>(
    createEmptyProductFormState,
  )
  const [filesToUpload, setFilesToUpload] = useState<File[]>([])
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [skuConflict, setSkuConflict] = useState<SKUConflict | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const isServiceByUnit = form.unit.trim().toLowerCase() === "usługa"
  const normalizedUnitWeight = normalizeProductUnitWeight(form.unit_weight_kg)
  const hasInvalidUnitWeight =
    form.unit_weight_kg.trim() !== "" && normalizedUnitWeight === undefined

  useEffect(() => {
    if (typeof window === "undefined") return
    try {
      const raw = window.sessionStorage.getItem(lastManufacturerStorageKey)
      if (!raw) return
      const parsed = JSON.parse(raw) as { id?: string; name?: string }
      if (typeof parsed.id === "string" && typeof parsed.name === "string") {
        setForm((prev) => ({
          ...prev,
          manufacturer_contragent_id: parsed.id ?? "",
          manufacturer_contragent_name: parsed.name ?? "",
          manufacturer: parsed.name ?? "",
        }))
      }
    } catch {
      // Ignore invalid saved manufacturer payload.
    }
  }, [])

  const isSkuErr = form.sku.trim() === ""
  const isNameErr = form.name.trim() === ""
  const isUnitErr = form.unit.trim() === ""
  const isManufacturerErr = form.manufacturer_contragent_id.trim() === ""

  const { mutate, isPending } = useMutation({
    mutationKey: ["products", "create"],
    mutationFn: (payload: ProductCreateDTO) =>
      ProductService.createProduct(payload),
    onSuccess: async (created) => {
      setSkuConflict(null)
      if (
        typeof window !== "undefined" &&
        form.manufacturer_contragent_id &&
        form.manufacturer_contragent_name
      ) {
        window.sessionStorage.setItem(
          lastManufacturerStorageKey,
          JSON.stringify({
            id: form.manufacturer_contragent_id,
            name: form.manufacturer_contragent_name,
          }),
        )
      }
      toast({ title: "Produkt został stworzony", status: "success" })
      if (filesToUpload.length > 0) {
        try {
          setUploadError(null)
          setUploadSuccess(null)
          setIsUploading(true)
          await ProductFilesService.uploadProductFiles(
            created.id,
            filesToUpload,
          )
          setUploadSuccess(`Załadowano ${filesToUpload.length} plik(ów).`)
          toast({
            title: "Pliki zostały dodane",
            status: "success",
          })
        } catch (err: any) {
          setUploadSuccess(null)
          setUploadError(
            err?.body?.error ?? err?.message ?? "Błąd wysyłania plików",
          )
          toast({
            title: "Nie udało się dodać plików",
            description: err?.body?.error ?? err?.message ?? "Spróbuj ponownie",
            status: "error",
          })
        } finally {
          setIsUploading(false)
        }
      }
      // Оновити список продуктів
      await qc.invalidateQueries({ queryKey: ["products", "list"] })
      onSuccess?.(created)
      // Очистити форму
      setForm(createEmptyProductFormState())
      setFilesToUpload([])
      setUploadError(null)
      setUploadSuccess(null)
      if (fileInputRef.current) fileInputRef.current.value = ""
    },
    onError: (err: any, submitted) => {
      const body = err?.body
      if (body?.code === "duplicate_product_sku") {
        const suggested =
          typeof body.suggested_sku === "string" && body.suggested_sku.trim()
            ? body.suggested_sku.trim()
            : null
        setSkuConflict({
          requested: submitted.sku.trim(),
          suggested,
        })
        toast({
          title: "SKU jest już zajęte",
          description: body?.error ?? "Produkt z podanym SKU już istnieje.",
          status: "error",
        })
        return
      }
      toast({
        title: "Błąd tworzenia",
        description: body?.error ?? err?.message ?? "Spróbuj ponownie",
        status: "error",
      })
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (
      isSkuErr ||
      isNameErr ||
      isUnitErr ||
      isManufacturerErr ||
      hasInvalidUnitWeight
    )
      return
    const extraDataObj = extraDataToJson(form.extra_data_items)

    const payload: ProductCreateWithUnitWeight = {
      sku: form.sku.trim(),
      name: form.name.trim(),
      manufacturer: form.manufacturer_contragent_name.trim(),
      manufacturer_contragent_id: form.manufacturer_contragent_id,
      supplier_ids: form.supplier_ids,
      unit: form.unit.trim(),
      EAN: form.EAN.trim() || undefined,
      default_min_qty: form.default_min_qty,
      unit_weight_kg: normalizedUnitWeight,
      catalog_price_percent: form.catalog_price_percent,
      description: form.description.trim(),
      extra_data: extraDataObj,
      is_service: isServiceByUnit,
      requires_batch_tracking: form.requires_batch_tracking,
      requires_expiration_date: form.requires_expiration_date,
    }
    mutate(payload)
  }

  return (
    <VStack as="form" onSubmit={handleSubmit} align="stretch" spacing={5}>
      <ProductFormFields
        form={form}
        setForm={setForm}
        manufacturerRequired
        errors={{
          manufacturer: isManufacturerErr
            ? "Pole Producenta jest obowiązkowe"
            : undefined,
          unit_weight_kg: hasInvalidUnitWeight
            ? "Wpisz dodatnią wagę z maksymalnie 4 miejscami po przecinku"
            : undefined,
          name: isNameErr ? "Pole Nazwa jest obowiązkowe" : undefined,
          sku: isSkuErr ? "Pole SKU jest obowiązkowe" : undefined,
          unit: isUnitErr ? "Pole J.m. jest obowiązkowe" : undefined,
        }}
      />

      {skuConflict && skuConflict.requested === form.sku.trim() && (
        <Alert
          status="warning"
          borderRadius="xl"
          alignItems={{ base: "flex-start", md: "center" }}
          flexDirection={{ base: "column", md: "row" }}
          gap={3}
        >
          <AlertIcon mt={{ base: 1, md: 0 }} />
          <Box flex="1">
            <Text fontWeight="semibold">
              SKU „{skuConflict.requested}” jest już zajęte.
            </Text>
            <Text fontSize="sm">
              {skuConflict.suggested
                ? `Następny dostępny wariant: ${skuConflict.suggested}`
                : "Wprowadź inne, nieużywane SKU."}
            </Text>
          </Box>
          {skuConflict.suggested && (
            <Button
              type="button"
              colorScheme="orange"
              size="sm"
              onClick={() => {
                setForm((previous) => ({
                  ...previous,
                  sku: skuConflict.suggested ?? previous.sku,
                }))
                setSkuConflict(null)
              }}
            >
              Użyj {skuConflict.suggested}
            </Button>
          )}
        </Alert>
      )}

      <Box
        borderWidth="1px"
        borderColor={panelBorder}
        borderRadius="2xl"
        bg={panelBg}
        p={{ base: 4, md: 5 }}
      >
        <VStack align="stretch" spacing={4}>
          <Box>
            <Text fontSize="sm" fontWeight="semibold">
              Dodatkowe ustawienia
            </Text>
            <Text fontSize="sm" color={mutedText} mt={1}>
              Dodaj pliki do produktu.
            </Text>
          </Box>
          <Divider borderColor={panelBorder} />

          <FormControl>
            <FormLabel>Pliki produktu</FormLabel>
            <input
              ref={fileInputRef}
              type="file"
              style={{ display: "none" }}
              multiple
              onChange={(e) => {
                setFilesToUpload(Array.from(e.target.files ?? []))
                setUploadError(null)
                setUploadSuccess(null)
              }}
            />
            <HStack spacing={2} wrap="wrap">
              <Button
                variant="outline"
                size="sm"
                borderRadius="xl"
                onClick={() => fileInputRef.current?.click()}
              >
                {filesToUpload.length > 0 ? "Zmień pliki" : "Wybierz pliki"}
              </Button>
              {filesToUpload.length > 0 && (
                <>
                  <Badge
                    colorScheme="blue"
                    borderRadius="full"
                    px={3}
                    py={1}
                    maxW="420px"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                  >
                    {filesToUpload.length === 1
                      ? filesToUpload[0].name
                      : `${filesToUpload.length} plików`}
                  </Badge>
                  <Button
                    size="xs"
                    variant="outline"
                    borderRadius="xl"
                    onClick={() => setFilesToUpload([])}
                  >
                    Usuń wszystkie
                  </Button>
                </>
              )}
            </HStack>
            {filesToUpload.length > 0 && (
              <List spacing={1} mt={2}>
                {filesToUpload.map((file, index) => (
                  <ListItem key={`${file.name}-${index}`}>
                    <HStack spacing={2}>
                      <Text fontSize="sm" noOfLines={1}>
                        {file.name}
                      </Text>
                      <Button
                        size="xs"
                        variant="outline"
                        borderRadius="xl"
                        onClick={() =>
                          setFilesToUpload((prev) =>
                            prev.filter((_, i) => i !== index),
                          )
                        }
                      >
                        Usuń
                      </Button>
                    </HStack>
                  </ListItem>
                ))}
              </List>
            )}
            {isUploading && (
              <VStack align="start" spacing={1} mt={2}>
                <Progress
                  size="sm"
                  isIndeterminate
                  colorScheme="teal"
                  borderRadius="full"
                  w="full"
                />
                <Text fontSize="xs" color={mutedText}>
                  Trwa ładowanie plików...
                </Text>
              </VStack>
            )}
            {uploadError && (
              <Alert status="error" mt={2} borderRadius="xl">
                <AlertIcon />
                {uploadError}
              </Alert>
            )}
            {uploadSuccess && (
              <Alert status="success" mt={2} borderRadius="xl">
                <AlertIcon />
                {uploadSuccess}
              </Alert>
            )}
          </FormControl>
        </VStack>
      </Box>

      <HStack
        justify="flex-end"
        pt={1}
        borderTopWidth="1px"
        borderColor={panelBorder}
      >
        <Button variant="ghost" onClick={onCancel} type="button">
          Anuluj
        </Button>
        <Button
          colorScheme="green"
          type="submit"
          isLoading={isPending || isUploading}
          isDisabled={
            isSkuErr ||
            isNameErr ||
            isUnitErr ||
            isManufacturerErr ||
            isUploading
          }
        >
          Utwórz
        </Button>
      </HStack>
    </VStack>
  )
}
